// Phone-mode rowing detector (29 Oct on-water settings). Pure logic, shared by
// useStrokeDetection and the recorded-scenario replay test.
import { BandPassFilter } from './bandPassFilter';

export const SAMPLE_RATE = 60;
export const PHONE_WINDOW_S = 8;               // autocorr + peak-to-peak window
export const PHONE_UPDATE_MS = 500;            // detector / rate update interval
export const PHONE_MIN_SPM = 12;               // autocorr search band
export const PHONE_MAX_STROKE_RATE = 50;
export const PHONE_AC_THRESHOLD = 0.35;        // open: autocorr peak ≥ 0.35
export const DETECTOR_P2P_MIN = 2.0;               // open: filtered p2p (8 s) ≥ 2.0 m/s²
export const DETECTOR_P2P_CLOSE = 1.5;             // close: p2p < 1.5 m/s² ...
export const DETECTOR_CLOSE_HOLD_MS = 6000;        // ... or GPS low, for 6 s in a row
export const DETECTOR_GPS_SPEED_MS = 1.0;          // GPS speed > 1.0 m/s ...
export const DETECTOR_GPS_HOLD_MS = 3000;          // ... held for 3 s
export const DETECTOR_GPS_MAX_ACCURACY_M = 10;     // only fixes with accuracy ≤ 10 m count
export const DETECTOR_GPS_STALE_MS = 3000;         // no good fix for 3 s → GPS not high
export const DETECTOR_CONSISTENT_UPDATES = 3;      // 3 consecutive readings ...
export const DETECTOR_CONSISTENT_SPM = 3;          // ... within ±3 spm before the first value
export const PHONE_FILTER_LOW_HZ = 0.08;
export const PHONE_FILTER_HIGH_HZ = 0.9;

// Autocorrelation of the band-pass filtered window; highest peak in 12–50 spm.
export function phoneAutocorr(filtered, sampleRate = SAMPLE_RATE) {
  const n = filtered.length;
  const minLag = Math.floor((60 / PHONE_MAX_STROKE_RATE) * sampleRate);
  const maxLag = Math.min(n - 2, Math.ceil((60 / PHONE_MIN_SPM) * sampleRate));
  if (n < sampleRate * 2 || maxLag <= minLag) return { peak: 0, rate: 0 };
  const mean = filtered.reduce((a, b) => a + b, 0) / n;
  const x = filtered.map((v) => v - mean);
  const variance = x.reduce((s, v) => s + v * v, 0);
  if (variance === 0) return { peak: 0, rate: 0 };
  const ac = (lag) => {
    let s = 0;
    for (let i = 0; i < n - lag; i += 1) s += x[i] * x[i + lag];
    return s / variance;
  };
  let prev = ac(minLag - 1);
  let cur = ac(minLag);
  let best = { peak: 0, lag: 0 };
  for (let lag = minLag; lag < maxLag; lag += 1) {
    const next = ac(lag + 1);
    if (cur > prev && cur >= next && cur > best.peak) best = { peak: cur, lag };
    prev = cur;
    cur = next;
  }
  if (!best.lag) return { peak: 0, rate: 0 };
  const rate = Math.min(PHONE_MAX_STROKE_RATE, Math.round((60 / (best.lag / sampleRate)) * 2) / 2);
  return { peak: best.peak, rate };
}

export const peakToPeak = (arr) => (arr.length ? Math.max(...arr) - Math.min(...arr) : 0);

// GPS: speed > 1.0 m/s for 3 s, using only fixes with accuracy ≤ 10 m.
export function createGpsCheck() {
  let since = null;
  let lastT = -Infinity;
  let used = null; // last fix the detector accepted {speed, accuracy}
  return {
    onFix(t, speed, accuracy) {
      if (typeof accuracy !== 'number' || !Number.isFinite(accuracy) || accuracy > DETECTOR_GPS_MAX_ACCURACY_M) return;
      lastT = t;
      used = { speed: typeof speed === 'number' ? speed : null, accuracy };
      if (typeof speed === 'number' && speed > DETECTOR_GPS_SPEED_MS) { if (since == null) since = t; } else since = null;
    },
    high: (t) => since != null && t - since >= DETECTOR_GPS_HOLD_MS && t - lastT < DETECTOR_GPS_STALE_MS,
    lastUsed: (t) => (used && t - lastT < DETECTOR_GPS_STALE_MS ? used : null),
    reset() { since = null; lastT = -Infinity; used = null; },
  };
}

// Open: AC ≥ 0.35 & p2p ≥ 2.0 & GPS high. Close: p2p < 1.5 or GPS not high for 6 s in a row.
// While open, a rate shows after 3 consecutive valid readings within ±3 spm, then
// follows each valid reading (holds the last value when a reading is not valid).
export function createRowingDetector() {
  let open = false;
  let showing = false;
  let shown = 0;
  let candidates = [];
  let p2pLowSince = null;
  let gpsLowSince = null;
  const close = () => { open = false; showing = false; shown = 0; candidates = []; p2pLowSince = null; gpsLowSince = null; };
  return {
    step({ t, rate, acPeak, p2p, gpsHigh }) {
      const acOk = acPeak >= PHONE_AC_THRESHOLD && rate > 0;
      const reasons = [];
      if (!open) {
        if (!acOk) reasons.push('ac');
        if (p2p < DETECTOR_P2P_MIN) reasons.push('p2p');
        if (!gpsHigh) reasons.push('gps');
        if (reasons.length) return { open, show: false, rate: 0, reasons };
        open = true;
      } else {
        p2pLowSince = p2p < DETECTOR_P2P_CLOSE ? (p2pLowSince ?? t) : null;
        gpsLowSince = !gpsHigh ? (gpsLowSince ?? t) : null;
        const p2pClose = p2pLowSince != null && t - p2pLowSince >= DETECTOR_CLOSE_HOLD_MS;
        const gpsClose = gpsLowSince != null && t - gpsLowSince >= DETECTOR_CLOSE_HOLD_MS;
        if (p2pClose || gpsClose) {
          close();
          return { open, show: false, rate: 0, reasons: [p2pClose && 'p2p-close', gpsClose && 'gps-close'].filter(Boolean) };
        }
      }
      if (acOk) {
        candidates = [...candidates, rate].slice(-DETECTOR_CONSISTENT_UPDATES);
        if (!showing && candidates.length === DETECTOR_CONSISTENT_UPDATES
          && Math.max(...candidates) - Math.min(...candidates) <= DETECTOR_CONSISTENT_SPM) showing = true;
        if (showing) shown = rate;
      } else if (!showing) {
        candidates = [];
      }
      return { open, show: showing, rate: showing ? shown : 0, reasons: showing ? [] : ['confirming'] };
    },
    reset: close,
  };
}

// Replays one recorded scenario ({t, a, gpsSpeed, gpsAcc} rows) through the same
// filter → window → detector chain the hook uses. Returns one entry per update.
export function replayScenario(rows) {
  const filter = new BandPassFilter(PHONE_FILTER_LOW_HZ, PHONE_FILTER_HIGH_HZ, SAMPLE_RATE);
  const gps = createGpsCheck();
  const detector = createRowingDetector();
  const win = [];
  const out = [];
  let lastUpdate = -Infinity;
  const size = SAMPLE_RATE * PHONE_WINDOW_S;
  for (const r of rows) {
    if (r.gpsAcc != null) gps.onFix(r.t, r.gpsSpeed, r.gpsAcc);
    win.push(filter.process(r.a));
    if (win.length > size) win.shift();
    if (win.length >= SAMPLE_RATE * 2 && r.t - lastUpdate >= PHONE_UPDATE_MS) {
      lastUpdate = r.t;
      const { peak, rate } = phoneAutocorr(win);
      const p2p = peakToPeak(win);
      const step = detector.step({ t: r.t, rate, acPeak: peak, p2p, gpsHigh: gps.high(r.t) });
      out.push({ t: r.t, acPeak: peak, p2p, ...step });
    }
  }
  return out;
}
